import { ChangeDetectorRef, Component, DestroyRef, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { SelectShareholderComponent } from "../../../controls/select-shareholder/select-shareholder.component";
import { InputNumberComponent } from "@shared/controls/input-number/input-number.component";
import { ValidationErrorComponent } from "@shared/controls/validation-error/validation-error.component";
import { FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ReusableFormGroupComponent } from '../../../reusable-form-groups/reusable-form-group/reusable-form-group.component';
import { CorporateHolderModel, IndividualHolderModel, SecurityRegistryRecord } from '@models/shares/security-registry-record';
import { NumbersValidators } from '../../../../shared/validators/numbers.validators';
import { setControlDisabled } from '../../../../shared/utils/set-control-disabled';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CompanySecurityCancellation,
  customErrorsOfShareCancellationNumbers,
  customSignErrors,
  totalNumberOfSharesCustomErrors
} from '../../../models/shares/company-security-cancellation.model';
import { SecurityTransactionIncremental } from '@models/security-transactions/security-transaction-incremental';
import { CompanyChangeData } from '@models/ui-models/company-change-data';
import { SecurityTransactionType } from '@models/security-transactions/security-transaction-type.enum';
import { groupSecurityRecordsByName } from '../../../../shared/utils/security.helper';
import { DividerComponent } from '@shared/components/commons/divider/divider.component';
import { SecurityHolding } from '@models/shares/security-holding';
import { ShareSecurityHoldingsDropdownComponent } from '../../../controls/share-security-holdings-dropdown/share-security-holdings-dropdown.component';
import {
  ShareholderWithNboSignComponent, ShareholderWithSignControls
} from "../../../controls/shareholder-with-nbo-sign/shareholder-with-nbo-sign.component";
import { startWith } from "rxjs";

export interface IShareCancellationFormGroupControls extends ShareholderWithSignControls {
  securityHoldingId: FormControl<string | null>,
  considerationPaid: FormControl<number | null>,
  numberOfSharesCancelled: FormControl<number | null>,
  totalPaid: FormControl<number | null>,
  totalUnpaid: FormControl<number | null>,
}

export type IShareCancellationFormGroup = FormGroup<IShareCancellationFormGroupControls>;

@Component({
  selector: 'app-share-cancellation-block',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DividerComponent,
    InputNumberComponent,
    ValidationErrorComponent,
    ShareSecurityHoldingsDropdownComponent,
    ShareholderWithNboSignComponent
  ],
  templateUrl: './share-cancellation-block.component.html',
  styleUrl: './share-cancellation-block.component.scss'
})
export class CompanySecurityCancellationBlock extends ReusableFormGroupComponent<IShareCancellationFormGroup> implements OnInit, OnChanges {
  @Input({ required: true }) securityRegistryRecords: SecurityRegistryRecord[] = [];
  @Input({ required: true }) companyChangeData!: CompanyChangeData;
  @Input({ required: true }) parentFormGroup!: FormGroup;

  destroyRef: DestroyRef = inject(DestroyRef);
  cdr = inject(ChangeDetectorRef);

  readonly totalNumberOfSharesCustomErrors = totalNumberOfSharesCustomErrors;
  readonly customErrorsOfShareCancellationNumbers = customErrorsOfShareCancellationNumbers;
  readonly customSignErrors = customSignErrors;

  selectedShareholderShareType: SecurityHolding | null = null;

  groupedSecurityRegistryRecords: SecurityRegistryRecord[][] = [];
  securityRegistryRecordsHoldings: SecurityHolding[] = [];

  selectedSecurityGroupHoldersGroupedByName;

  public override ngOnInit(): void {
    super.ngOnInit();
    this.setupFormChangeListeners();
    this.setCustomFormValidators();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['securityRegistryRecords']) {
      this.groupedSecurityRegistryRecords = groupSecurityRecordsByName(this.securityRegistryRecords);
    }
  }

  static override defineFormControls(): IShareCancellationFormGroupControls {
    return {
      securityRegistryRecordGroup: new FormControl<SecurityRegistryRecord[] | null>(null, [Validators.required]),
      securityHoldingId: new FormControl<string | null>(null, [Validators.required]),
      considerationPaid: new FormControl<number | null>(0, [Validators.required, NumbersValidators.strictMin(0)]),
      numberOfSharesCancelled: new FormControl<number | null>(0, [Validators.required, NumbersValidators.min(0)]),
      totalPaid: new FormControl<number | null>(1, [Validators.required, NumbersValidators.min(0)]),
      totalUnpaid: new FormControl<number | null>(0, [Validators.required, NumbersValidators.min(0)]),
    };
  }

  setupFormChangeListeners() {
    this.listenShareholderChanges();
    this.listenShareTypeChanges();
    this.listenNumberOfSharesCancelledChange();
  }


  private listenShareholderChanges(): void {
    this.form.controls.securityRegistryRecordGroup.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(this.form.controls.securityRegistryRecordGroup.value)
      )
      .subscribe((recordGroup) => {
        if (recordGroup === null) {
          this.securityRegistryRecordsHoldings = [];
          this.form.controls.securityHoldingId.patchValue(null);
          return;
        }
      });
  }

  private listenShareTypeChanges(): void {
    this.form.controls.securityHoldingId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((securityHoldingId) => {
        this.selectedShareholderShareType = this.securityRegistryRecordsHoldings.find((holding) => holding.securityHoldingId === securityHoldingId) ?? null;
        if (this.selectedShareholderShareType) {
          // cancelled shares >=0 && <= number of shares
          this.setNumberControlValidators(this.form.controls.numberOfSharesCancelled, () => (this.selectedShareholderShareType?.number ?? 0));
          this.form.controls.numberOfSharesCancelled.addValidators([NumbersValidators.isInteger]);
          // total paid >=0 && <= prefilled total paid
          this.setNumberControlValidators(this.form.controls.totalPaid, () => (this.selectedShareholderShareType?.paid ?? 0));
          // total unpaid >=0 && <= prefilled total unpaid
          this.setNumberControlValidators(this.form.controls.totalUnpaid, () => (this.selectedShareholderShareType?.unpaid ?? 0));
        }

        this.form.controls.numberOfSharesCancelled.patchValue(this.selectedShareholderShareType?.number ?? 0);
      });
  }

  private listenNumberOfSharesCancelledChange(): void {
    this.form.controls.numberOfSharesCancelled.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((numberOfSharesCancelled) => {
        if (numberOfSharesCancelled === null || this.selectedShareholderShareType === null) {
          return;
        }

        this.form.controls.considerationPaid.patchValue(numberOfSharesCancelled * this.selectedShareholderShareType.securityType.nominalPrice);
        this.form.controls.totalPaid.patchValue(numberOfSharesCancelled * this.selectedShareholderShareType.securityType.nominalPrice);
        this.form.controls.totalUnpaid.patchValue(numberOfSharesCancelled * this.selectedShareholderShareType.securityType.unpaidAmount);

        setControlDisabled(this.form.controls.totalPaid, numberOfSharesCancelled >= this.selectedShareholderShareType.number);
        setControlDisabled(this.form.controls.totalUnpaid, numberOfSharesCancelled >= this.selectedShareholderShareType.number);
      });
  }

  setCustomFormValidators(): void {
    const nonBeneficialOwnersSignedValidator: ValidatorFn = () => {
      if (!this.selectedSecurityRecord || !this.selectedShareholderShareType) {
        return null;
      }

      const nonBeneficialOwners = this.selectedSecurityRecord?.holders
        .filter((holder) => !holder.details.isBeneficialOwner);

      if (!nonBeneficialOwners?.length) {
        return null;
      }

      return nonBeneficialOwners.every(holder => Boolean(holder.details.beneficialOwner))
        ? null
        : { nonBeneficialOwnersNotSigned: true };
    };

    const paidTotalValidator: ValidatorFn = () => {
      if (this.selectedShareholderShareType === null || !this.form.value.totalPaid || !this.form.value.numberOfSharesCancelled) {
        return null;
      }
      // (Math.abs(prefilled total paid - total paid))/prefilled total paid < 0.05

      const calculatedTotalPaid = this.selectedShareholderShareType.securityType.nominalPrice * this.form.value.numberOfSharesCancelled;
      return (Math.abs(calculatedTotalPaid - this.form.value.totalPaid) / calculatedTotalPaid) < 0.05
        ? null
        : { paidChangeIsBiggerThan5Percent: true };
    };

    const unpaidTotalValidator: ValidatorFn = () => {
      if (this.selectedShareholderShareType === null || !this.form.value.totalUnpaid || !this.form.value.numberOfSharesCancelled) {
        return null;
      }
      // (Math.abs(prefilled total unpaid - total unpaid))/prefilled total unpaid < 0.05

      const calculatedTotalPaid = this.selectedShareholderShareType.securityType.unpaidAmount * this.form.value.numberOfSharesCancelled;
      return (Math.abs(calculatedTotalPaid - this.form.value.totalUnpaid) / calculatedTotalPaid) < 0.05
        ? null
        : { unpaidChangeIsBiggerThan5Percent: true };
    };

    // total paid + total unpaid > 0
    const paidUnpaidSumValidator: ValidatorFn = () => {
      const totalPaid = this.form.controls.totalPaid.getRawValue();
      const totalUnpaid = this.form.controls.totalUnpaid.getRawValue();
      if (totalPaid === null || totalUnpaid === null) {
        return null;
      }

      return (totalPaid + totalUnpaid) > 0
        ? null
        : { paidUnpaidSumIsNotPositive: true };
    };
    this.form.setValidators([
      paidTotalValidator,
      unpaidTotalValidator,
      paidUnpaidSumValidator
    ]);

    this.parentFormGroup.setValidators([nonBeneficialOwnersSignedValidator]);
  }

  get formShareNumberErrorMessageHidden(): boolean {
    return Boolean(this.form.controls.numberOfSharesCancelled?.untouched
      && this.form.controls.totalPaid?.untouched
      && this.form.controls.totalUnpaid?.untouched
      && !this.form?.errors);
  }

  public buildShareFormChange(): CompanySecurityCancellation {
    const selectedSecurityRecord = this.groupedSecurityRegistryRecords
      .find(group => group.some(record => record.holdings.some(holder => holder.securityHoldingId === this.form.value.securityHoldingId)))
      ?.find(record => record.holdings.some(holder => holder.securityHoldingId === this.form.value.securityHoldingId));

    const cancellation = new SecurityTransactionIncremental({
      securityHoldingId: this.form.value.securityHoldingId ?? null,
      transactionType: SecurityTransactionType.Cancellation,
      considerationPaid: this.form.value.considerationPaid!,
      numberIncrease: -this.form.value.numberOfSharesCancelled!,
      paidIncrease: -this.form.controls.totalPaid.getRawValue()!,
      unpaidIncrease: -this.form.controls.totalUnpaid.getRawValue()!,
      entityId: this.companyChangeData.entityId,
      securityType: this.selectedShareholderShareType!.securityType,
      securityTypeId: this.selectedShareholderShareType!.securityType.securityTypeId,
      relationshipIds: selectedSecurityRecord?.holders.map(h => h.relationshipId)
    });

    return new CompanySecurityCancellation({ cancellation });
  }

  setupChange(change: CompanySecurityCancellation) {
    const selectedSecurityRegistryGroup =
      this.groupedSecurityRegistryRecords.find((group) => group.some((record) => record?.holdings.some((holder) => holder.securityHoldingId === change.cancellation.securityHoldingId)));

    this.form.controls.securityRegistryRecordGroup.patchValue(selectedSecurityRegistryGroup ?? null);

    if (selectedSecurityRegistryGroup) {
      const selectedSecurityHolding = selectedSecurityRegistryGroup
        .flatMap((record) => record.holdings)?.find(holding => holding.securityHoldingId === change.cancellation.securityHoldingId);

      if (selectedSecurityHolding !== undefined) {
        this.onSelectCurrentShareholder(selectedSecurityRegistryGroup);
        this.cdr.detectChanges();
        this.form.controls.securityHoldingId.patchValue(selectedSecurityHolding?.securityHoldingId ?? null);

        this.form.patchValue({
          considerationPaid: change.cancellation.considerationPaid,
          totalPaid: -change.cancellation.paidIncrease,
          totalUnpaid: -change.cancellation.unpaidIncrease,
          numberOfSharesCancelled: -change.cancellation.numberIncrease
        });
      }
    }
  }

  onSelectCurrentShareholder(recordGroup: SecurityRegistryRecord[], shouldPrefillSecurityType = true): void {
    this.securityRegistryRecordsHoldings = (recordGroup ?? [])?.flatMap((group) => group.holdings);
    this.selectedShareholderShareType = this.securityRegistryRecordsHoldings[0] ?? null;
    this.cdr.detectChanges();

    if (shouldPrefillSecurityType) {
      this.form.controls.securityHoldingId.patchValue(this.securityRegistryRecordsHoldings[0].securityHoldingId ?? null);
    }

    const record = (this.form.controls.securityRegistryRecordGroup.value?.flatMap((record) => record.holders) ?? [])
      .reduce((acc, holder) => {
        const key = holder.name;

        if (acc[key]) {
          acc[key].push(holder);
        } else {
          acc[key] = [holder];
        }

        return acc;
      }, {} as Record<string, (IndividualHolderModel | CorporateHolderModel)[]>)

    this.selectedSecurityGroupHoldersGroupedByName =
      Object.entries(record).map(([key, value]) => ({
        key,
        value,
        relationshipIds: value.map(v => v.relationshipId)
      }));
  }

  private get selectedSecurityRecord(): SecurityRegistryRecord | null {
    return this.groupedSecurityRegistryRecords
      .find((group) => group.some((record) => record.holdings.some(holder => holder.securityHoldingId === this.form.value.securityHoldingId)))
      ?.find((record) => record.holdings.some((holder) => holder.securityHoldingId === this.form.value.securityHoldingId)) ?? null;
  }

  private setNumberControlValidators(control: FormControl, maxFn: () => number, min = 0): void {
    control.setValidators([Validators.required, NumbersValidators.min(min), NumbersValidators.relativeMax(maxFn.bind(this))]);
    control.updateValueAndValidity();
  }

  get selectControlFormGroupPart(): FormGroup<ShareholderWithSignControls> {
    return this.form as any as FormGroup<ShareholderWithSignControls>;
  }

  signDataSaved(): void {
    this.form.updateValueAndValidity();
  }
}
